import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { comics, categories as allCategories } from '@/mocks/comics';
import ComicCard from '@/components/feature/ComicCard';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PageHead from '@/components/feature/PageHead';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');
  const [displayComics, setDisplayComics] = useState(comics);

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeCategory]);

  useEffect(() => {
    if (activeCategory === 'all') {
      setDisplayComics(comics);
    } else {
      const cat = allCategories.find((c) => c.slug === activeCategory);
      if (cat) {
        setDisplayComics(comics.filter((comic) => comic.category === cat.name));
      }
    }
  }, [activeCategory]);

  const currentCat = allCategories.find((c) => c.slug === activeCategory);
  const catName = currentCat ? currentCat.name : '全部';

  const catJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${catName}漫画 - 甜柚漫画`,
    description: `甜柚漫画${catName}分类，海量${catName}漫画免费阅读，下拉式竖屏阅读体验`,
    url: `https://www.tianyoucomics.com/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '甜柚漫画',
      url: 'https://www.tianyoucomics.com',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(catJsonLd)}</script>
      <PageHead
        title={`${catName}漫画 - 甜柚漫画 | 免费${catName}漫画下拉式阅读`}
        description={`甜柚漫画${catName}分类，海量${catName}漫画免费阅读，支持下拉式阅读、无限滚动、手机竖屏畅读。尽在甜柚漫画网。`}
        keywords={`${catName}漫画,甜柚漫画,下拉式阅读,免费漫画,热门漫画,手机漫画`}
        canonicalPath={`/category/${activeCategory}`}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '漫画分类', path: '/category/all' },
                { label: catName },
              ]}
            />
          </div>

          <section className="py-6 md:py-10 bg-background-100">
            <div className="px-4 md:px-6 max-w-7xl mx-auto">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-6">
                {catName}漫画
              </h1>

              <div className="flex flex-wrap gap-2 mb-8">
                {allCategories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>

              {displayComics.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5" data-product-shop>
                  {displayComics.map((comic) => (
                    <ComicCard key={comic.id} {...comic} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                    <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无漫画，看看其他分类吧</p>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}